'use client';

import React, { useRef, useState, useEffect } from 'react';
import { Loader2, ArrowLeft, MailCheck, Smartphone, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AuthStep } from './SignUpLoginScreen';

interface OTPVerificationProps {
  step: AuthStep;
  email: string;
  onEmailSuccess: () => void;
  onPhoneSuccess: () => void;
  onBack: () => void;
}

const OTP_LENGTH = 6;
const DEMO_OTP = '123456';

export default function OTPVerification({
  step,
  email,
  onEmailSuccess,
  onPhoneSuccess,
  onBack,
}: OTPVerificationProps) {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isEmailStep = step === 'otp-email';

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, [step]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (newOtp.every((d) => d !== '') && newOtp.join('').length === OTP_LENGTH) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length === OTP_LENGTH) {
      setOtp(pasted.split(''));
      inputRefs.current[OTP_LENGTH - 1]?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (code: string) => {
    setIsLoading(true);
    setError('');
    // Backend integration point: POST /api/v1/auth/email/verify or /api/v1/auth/phone/verify-otp
    await new Promise((r) => setTimeout(r, 1000));

    if (code === DEMO_OTP) {
      setIsVerified(true);
      toast.success(isEmailStep ? 'Email verified!' : 'Mobile verified!');
      setTimeout(() => {
        if (isEmailStep) onEmailSuccess();
        else onPhoneSuccess();
      }, 900);
    } else {
      setError('Incorrect OTP. Use 123456 for the demo.');
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
    setIsLoading(false);
  };

  const handleResend = async () => {
    setResendSeconds(30);
    setError('');
    setOtp(Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    // Backend integration point: POST /api/v1/auth/email/send-verification or /api/v1/auth/phone/send-otp
    toast.success('OTP resent — use 123456 for the demo');
  };

  const progressSteps = ['Account', 'Email', 'Mobile'];
  const currentProgress = isEmailStep ? 1 : 2;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
        Back
      </button>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {progressSteps.map((s, i) => (
          <React.Fragment key={`otp-progress-${s}`}>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-2xs font-bold transition-all ${
                  i < currentProgress
                    ? 'bg-primary text-white'
                    : i === currentProgress
                    ? 'bg-primary-light border-2 border-primary text-primary' :'bg-muted text-muted-foreground'
                }`}
              >
                {i < currentProgress ? <CheckCircle2 size={12} /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i <= currentProgress ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s}
              </span>
            </div>
            {i < progressSteps.length - 1 && (
              <div className={`flex-1 h-px ${i < currentProgress ? 'bg-primary' : 'bg-border'} transition-colors`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Icon + heading */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isEmailStep ? 'bg-primary-light' : 'bg-gold-light'}`}>
          {isEmailStep
            ? <MailCheck size={22} className="text-primary" />
            : <Smartphone size={22} className="text-gold" />
          }
        </div>
        <div>
          <h2 className="font-display text-xl font-semibold text-foreground">
            {isEmailStep ? 'Verify your email' : 'Verify your mobile'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isEmailStep
              ? `We sent a 6-digit code to ${email}`
              : 'We sent a 6-digit code to your mobile number'
            }
          </p>
        </div>
      </div>

      {/* OTP inputs */}
      <div className="flex gap-2 mb-5" onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input
            key={`otp-digit-${i}`}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={isLoading || isVerified}
            className={`otp-input ${digit ? 'filled' : ''} ${error ? '!border-error !bg-error/4' : ''} ${isVerified ? '!border-success !bg-success-bg' : ''}`}
            aria-label={`OTP digit ${i + 1}`}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 mb-4 fade-in">
          <AlertCircle size={14} className="text-error flex-shrink-0" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Success */}
      {isVerified && (
        <div className="flex items-center gap-2 mb-4 fade-in">
          <CheckCircle2 size={14} className="text-success flex-shrink-0" />
          <p className="text-sm text-success font-medium">Verified successfully!</p>
        </div>
      )}

      {/* Demo hint */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted mb-5">
        <span className="text-xs text-muted-foreground">Demo OTP:</span>
        <code className="text-xs font-bold text-primary tracking-widest">1 2 3 4 5 6</code>
      </div>

      {/* Verify button */}
      <button
        onClick={() => handleVerify(otp.join(''))}
        disabled={otp.some((d) => !d) || isLoading || isVerified}
        className="w-full h-11 rounded-xl brand-gradient text-white text-sm font-semibold shadow-primary-custom btn-press disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Verifying…
          </>
        ) : isVerified ? (
          <>
            <CheckCircle2 size={16} />
            Verified!
          </>
        ) : (
          'Verify Code'
        )}
      </button>

      {/* Resend */}
      <p className="text-sm text-center text-muted-foreground">
        Didn't receive it?{' '}
        {resendSeconds > 0 ? (
          <span className="text-muted-foreground/60">Resend in {resendSeconds}s</span>
        ) : (
          <button
            onClick={handleResend}
            className="text-primary font-semibold hover:underline transition-colors"
          >
            Resend code
          </button>
        )}
      </p>
    </div>
  );
}